import { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowDown2, ArrowUp2 } from "iconsax-reactjs";
import AdminLayoutSideBarItemDropdown from "./AdminLayoutSideBarItemDropdown";

interface SideBarItemProps {
  item: {
    id: string;
    label: string;
    icon?: React.ReactNode;
    path?: string;
    notifition?: string;
    children?: {
      id: string;
      label: string;
      path?: string;
      notifition?: string;
    }[];
  };
  notifications: string[];
}

export default function AdminLayoutSideBarItem({
  item,
  notifications,
}: SideBarItemProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const hasChildren = Array.isArray(item.children) && item.children.length > 0;

  const isChildActive = useMemo(
    () =>
      hasChildren &&
      (item.children || []).some(
        (child) =>
          !!child.path &&
          (location.pathname === child.path ||
            location.pathname.startsWith(`${child.path}/`))
      ),
    [hasChildren, item.children, location.pathname]
  );

  const [isOpen, setIsOpen] = useState<boolean>(Boolean(isChildActive));

  useEffect(() => {
    if (isChildActive) setIsOpen(true);
  }, [isChildActive]);

  const itemNotificationKeys = useMemo(
    () =>
      item.notifition
        ?.trim()
        .split(/\s+/)
        .filter((key) => key.length > 0) || [],
    [item.notifition]
  );

  const hasItemNotification = itemNotificationKeys.some((key) =>
    notifications.includes(key)
  );

  const hasChildNotification =
    hasChildren &&
    (item.children || []).some((child) => {
      if (!child.notifition) return false;
      const childNotifKey = child.notifition.trim();
      return childNotifKey.length > 0 && notifications.includes(childNotifKey);
    });

  const showNotif = hasItemNotification || hasChildNotification;
  const isActive = location.pathname === item.path || isChildActive;

  const clickHandler = (e: React.MouseEvent) => {
    e.preventDefault();

    if (hasChildren) {
      setIsOpen((prev) => !prev);
    } else if (item.id === "logout") {
      localStorage.clear();
      navigate("/");
    } else if (item.path) {
      navigate(item.path);
    }
  };

  return (
    <div className="flex w-full flex-col overflow-hidden">
      <button
        onClick={clickHandler}
        className="flex w-full items-center rounded-xl px-4 py-2.5 relative transition-colors duration-200 hover:bg-primaryVar-50"
      >
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex w-full items-center gap-2 relative">
            <span
              className={`${isActive ? "text-primaryVar" : "text-gray-500"}`}
            >
              {item.icon}
            </span>

            <span
              className={`text-right text-sm ${
                isActive ? "text-primaryVar" : "text-gray-500"
              }`}
            >
              {item.label}
            </span>
            {showNotif && (
              <span className="h-2 w-2 rounded-full bg-errorVar absolute right-40 top-1/2 -translate-y-1/2"></span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {hasChildren &&
              (isOpen ? (
                <span
                  className={`${isActive ? "text-primaryVar" : "text-gray-500"}`}
                >
                  <ArrowUp2 size={20} />
                </span>
              ) : (
                <span
                  className={`${isActive ? "text-primaryVar" : "text-gray-500"}`}
                >
                  <ArrowDown2 size={20} />
                </span>
              ))}
          </div>
        </div>
      </button>
      {hasChildren && (
        <AdminLayoutSideBarItemDropdown
          isOpen={isOpen}
          data={item.children || []}
          notifications={notifications}
          parentNotificationKeys={itemNotificationKeys}
        />
      )}
    </div>
  );
}
